/**
 * Release 2 S3 (A) — "Save as smart board" for the board filter bar and the search page. On a board the proposed
 * definition watches that board (sub-boards included when the page lists them) with the active filters.
 */
import { useState } from 'react';
import { useInRouterContext, useParams, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import type { SearchFilters } from '@/lib/api/types';
import { useBoard } from '@/lib/api/queries';
import { usePermission } from '@/features/workspace/usePermission';
import type { BoardFilters } from '@/features/search/predicates/serialize';
import { cx } from '@/lib/format';
import { SmartBoardDialog } from './SmartBoardDialog';
import { definitionFromBoard, definitionFromSearch, type SmartDraft } from './definition';

function SaveButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <button type="button" className={cx('inline-flex items-center gap-1 text-accent hover:underline', className)} onClick={onClick} data-save-smart>
      <Sparkles size={11} /> Save as smart board
    </button>
  );
}

/** In the board filter bar's chip row: only on a static board, for a member who may create boards. */
export function SaveBoardFiltersAsSmart({ filters }: { filters: BoardFilters }) {
  // the filter bar also renders outside a router (unit tests); there is no board to save from there
  return useInRouterContext() ? <SaveBoardFilters filters={filters} /> : null;
}

function SaveBoardFilters({ filters }: { filters: BoardFilters }) {
  const { boardId } = useParams();
  const [params] = useSearchParams();
  const board = useBoard(boardId);
  const canCreate = usePermission('MANAGE_BOARD');
  const [draft, setDraft] = useState<SmartDraft | null>(null);
  if (!boardId || !board.data || board.data.kind === 'smart' || !canCreate) return null;
  return (
    <>
      <SaveButton className="ml-2" onClick={() => setDraft(definitionFromBoard(boardId, params.get('deep') === '1', filters))} />
      {draft && <SmartBoardDialog mode="create" initial={draft} libraryId={board.data.library_id} onClose={() => setDraft(null)} />}
    </>
  );
}

/** On the search page: the facet filters become the definition; the free text is dropped (and said so). */
export function SaveSearchAsSmart({ filters, query }: { filters: SearchFilters; query: string }) {
  const canCreate = usePermission('MANAGE_BOARD');
  const [draft, setDraft] = useState<SmartDraft | null>(null);
  if (!canCreate) return null;
  return (
    <>
      <SaveButton onClick={() => setDraft(definitionFromSearch(filters))} />
      {draft && <SmartBoardDialog mode="create" initial={draft} note={query.trim() ? `The search text “${query.trim()}” is not part of a smart board; only the filters are saved.` : undefined} onClose={() => setDraft(null)} />}
    </>
  );
}
